import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import './GuestListPage.css';
import { eventService, guestService } from '../services/db';
import ConfirmationDialog from '../components/ConfirmationDialog';

function GuestListPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth(); // Get currentUser directly from auth context
  const [event, setEvent] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, attending, declined, pending
  const [error, setError] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null);
  
  useEffect(() => {
    const loadData = async () => {
      try {
        if (!eventId) {
          setError("Event ID is missing");
          setLoading(false);
          return;
        }
        
        const currentEvent = await eventService.getById(parseInt(eventId));
        
        if (!currentEvent) {
          setError("Event not found");
          setLoading(false);
          return;
        }
        
        setEvent(currentEvent);
        
        // Load guests from database
        const eventGuests = await guestService.getByEventId(parseInt(eventId));
        
        // Format guest data
        const formattedGuests = eventGuests.map(guest => ({
          ...guest,
          source: guest.invitationId ? 'RSVP Form' : 'Manually Added'
        }));
        
        setGuests(formattedGuests);
        setLoading(false);
      } catch (error) {
        console.error("Error loading event data:", error);
        setError("Failed to load event data. Please try again.");
        setLoading(false);
      }
    };
    
    loadData();
  }, [eventId, navigate]);
  
  // Filter guests based on selected filter
  const filteredGuests = () => {
    if (filter === 'all') return guests;
    return guests.filter(guest => guest.rsvpStatus === filter);
  };
  
  // Add a new guest manually
  const addGuest = (e) => {
    e.preventDefault();
    const name = e.target.name.value;
    const email = e.target.email.value;
    
    if (!name) return;
    
    const newGuest = {
      id: `guest-${Date.now()}`,
      name,
      email,
      rsvpStatus: 'pending',
      invitationId: null, // Manual addition
      createdAt: new Date().toISOString()
    };
    
    const updatedGuests = [...guests, newGuest];
    setGuests(updatedGuests);
    
    // Save to localStorage
    const savedGuests = JSON.parse(localStorage.getItem('eventGuests') || '{}');
    savedGuests[eventId] = updatedGuests;
    localStorage.setItem('eventGuests', JSON.stringify(savedGuests));
    
    // Reset form
    e.target.reset();
  };
  
  // Update guest RSVP status
  const updateGuestStatus = (guestId, status) => {
    const updatedGuests = guests.map(guest => 
      guest.id === guestId ? { ...guest, rsvpStatus: status } : guest
    );
    
    setGuests(updatedGuests);
    
    // Save to localStorage
    const savedGuests = JSON.parse(localStorage.getItem('eventGuests') || '{}');
    savedGuests[eventId] = updatedGuests;
    localStorage.setItem('eventGuests', JSON.stringify(savedGuests));
  };
  
  // Delete a guest
  const deleteGuest = (guestId) => {
    setGuestToDelete(guestId);
    setIsDeleteDialogOpen(true);
  };
  
  const confirmDeleteGuest = () => {
    const updatedGuests = guests.filter(guest => guest.id !== guestToDelete);
    setGuests(updatedGuests);
    
    // Save to localStorage
    const savedGuests = JSON.parse(localStorage.getItem('eventGuests') || '{}');
    savedGuests[eventId] = updatedGuests;
    localStorage.setItem('eventGuests', JSON.stringify(savedGuests));
    
    // Close the dialog
    setIsDeleteDialogOpen(false);
    setGuestToDelete(null);
  };
  
  const cancelDeleteGuest = () => {
    // Close the dialog without deleting
    setIsDeleteDialogOpen(false);
    setGuestToDelete(null);
  };
  
  if (loading) {
    return <div className="loading">Loading guest list...</div>;
  }
  
  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate("/events")} className="back-btn">
          Back to Events
        </button>
      </div>
    );
  }
  
  return (
    <div className="guest-list-page">
      <Header />
      
      <div className="guest-list-container">
        <div className="guest-list-header">
          <div className="back-navigation">
            <Link to={`/events/${eventId}`} className="back-link">
              ← Back to {event?.name || 'Event'}
            </Link>
            <h1>Guest List</h1>
          </div>
          
          <div className="guest-stats">
            <div className="stat-item">
              <span className="stat-value">
                {guests.reduce((total, guest) => {
                  // Add the guest count (person + any additional guests)
                  return total + (parseInt(guest.guestCount) || 1);
                }, 0)}
              </span>
              <span className="stat-label">Total Guests</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">
                {guests.reduce((total, guest) => {
                  // For attending count, only count if status is attending and include their guests
                  return total + (guest.rsvpStatus === 'attending' ? (parseInt(guest.guestCount) || 1) : 0);
                }, 0)}
              </span>
              <span className="stat-label">Attending</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{guests.length}</span>
              <span className="stat-label">RSVP Submissions</span>
            </div>
          </div>
        </div>
        
        <div className="guest-list-content">
          <div className="guest-list-actions">
            <div className="filter-buttons">
              <button 
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button 
                className={`filter-btn ${filter === 'attending' ? 'active' : ''}`}
                onClick={() => setFilter('attending')}
              >
                Attending
              </button>
              <button 
                className={`filter-btn ${filter === 'declined' ? 'active' : ''}`}
                onClick={() => setFilter('declined')}
              >
                Declined
              </button>
              <button 
                className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
                onClick={() => setFilter('pending')}
              >
                Pending
              </button>
            </div>
            
            <div className="add-guest-form-container">
              <h3>Add Guest</h3>
              <form onSubmit={addGuest} className="add-guest-form">
                <input 
                  type="text" 
                  name="name" 
                  placeholder="Guest Name" 
                  required 
                />
                <input 
                  type="email" 
                  name="email" 
                  placeholder="Email Address (optional)" 
                />
                <button type="submit" className="add-btn">Add</button>
              </form>
            </div>
          </div>
          
          <div className="guest-list-table">
            {filteredGuests().length === 0 ? (
              <div className="no-guests-message">
                {filter === 'all' 
                  ? 'No guests added yet. Add your first guest or send invitations.'
                  : `No guests with status: ${filter}`
                }
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGuests().map(guest => (
                    <tr key={guest.id}>
                      <td>{guest.name}</td>
                      <td>{guest.email || '-'}</td>
                      <td>
                        <select 
                          value={guest.rsvpStatus}
                          onChange={(e) => updateGuestStatus(guest.id, e.target.value)}
                          className={`status-select status-${guest.rsvpStatus}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="attending">Attending</option>
                          <option value="declined">Declined</option>
                        </select>
                      </td>
                      <td>{guest.source}</td>
                      <td>
                        <button 
                          className="delete-guest-btn" 
                          onClick={() => deleteGuest(guest.id)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM19 4H15.5L14.5 3H9.5L8.5 4H5V6H19V4Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        title="Remove Guest"
        message="Are you sure you want to remove this guest? This action cannot be undone."
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={confirmDeleteGuest}
        onCancel={cancelDeleteGuest}
        type="delete"
      />
    </div>
  );
}

export default GuestListPage; 